import {
  COMBINE_GRAPHS_MODE, DEFAULT_COMBINE_GRAPHS_MODE, DurationInput, Field, FormFragmentSchema, RadioGroupInput
} from 'apstra-ui-common';
import {compact, isFunction, map} from 'lodash';
import {ComponentProps, ComponentType, useMemo} from 'react';

import AggregationInput, {
  aggregationIntervalOptions, aggregationIntervalOptionsWithOff
} from '../../components/AggregationInput';
import AggregationTypeInput from '../components/AggregationTypeInput';
import StageWidgetValueColumnNameInput from '../components/StageWidgetValueColumnNameInput';
import {ProbePattern} from '../checkForPatterns';
import {
  DEFAULT_STAGE_DATA_SOURCE, DEFAULT_STAGE_TIME_SERIES_AGGREGATION, DEFAULT_STAGE_TIME_SERIES_DURATION,
  STAGE_DATA_SOURCE
} from '../consts';
import {getPossibleAggregationTypes, getPossibleValueColumnOptions, processorCanRaiseAnomalies,
  processorCanRaiseWarnings} from '../stageUtils';
import {ProbeProcessor, ProbeStage, RenderingStrategy, StageDataSource} from '../types';

export const useStageOptionsSchema = ({
  DataSourceComponent = RadioGroupInput,
  aggregationType,
  dataSource,
  descriptionPosition,
  canShowTelemetryServiceWarningsDataSource,
  isProbeOperational,
  isSpotlightMode,
  patternDescription,
  processor,
  renderingStrategy,
  shouldIncludeLabels,
  showContext,
  stage,
  valueColumnName,
}: {
  DataSourceComponent?: ComponentType;
  aggregationType: string;
  dataSource: StageDataSource;
  descriptionPosition?: ComponentProps<typeof Field>['descriptionPosition'];
  canShowTelemetryServiceWarningsDataSource?: boolean;
  isProbeOperational: boolean;
  isSpotlightMode: boolean;
  patternDescription: ProbePattern | null;
  processor: ProbeProcessor | undefined;
  renderingStrategy: RenderingStrategy | undefined | null;
  shouldIncludeLabels?: boolean;
  showContext: boolean;
  stage: ProbeStage | undefined;
  valueColumnName: string;
}): FormFragmentSchema[] => {
  const stageHasPersistedData = stage?.enable_metric_logging ?? false;
  const stageDataSourceIsTimeSeries = dataSource === STAGE_DATA_SOURCE.time_series;
  const usePatternWithPersistedData = patternDescription?.shouldFetchPersistedStageData && showContext;
  const usePatternWithRawPersistedData = patternDescription?.shouldFetchRawPersistedStageData && showContext;
  const stageHasAlternativeRenderingStrategy = !!(stage && patternDescription) &&
    (!stageDataSourceIsTimeSeries || patternDescription.renderingStrategy?.alwaysUseContext);
  const alwaysUseContext = patternDescription?.renderingStrategy?.alwaysUseContext;
  const stageCustomizationAvailable = !patternDescription?.renderingStrategy?.noCustomization;
  const stageMayHaveAnomalies = stageCustomizationAvailable && processor &&
      processorCanRaiseAnomalies(processor);

  const aggregationTypeOptions = useMemo(() => {
    if (!stage) return [];
    return map(getPossibleAggregationTypes(stage, valueColumnName), (type) => ({
      const: type, title: type,
    }));
  }, [valueColumnName, stage]);

  const mayCombineGraphs = useMemo(() => {
    if (!renderingStrategy || !stage || isSpotlightMode) return false;
    const valueSchema = stage.values[valueColumnName];
    return isFunction(renderingStrategy?.graphCombiningAvailable) ?
      renderingStrategy?.graphCombiningAvailable(valueSchema) :
      renderingStrategy?.graphCombiningAvailable ?? false;
  }, [isSpotlightMode, renderingStrategy, stage, valueColumnName]);

  const valueColumnOptions = useMemo(() => map(
    stage ? getPossibleValueColumnOptions(stage) : [],
    ({value, text}) => ({const: value, title: text})
  ), [stage]);

  const dataSourceOptions = useMemo(() => compact([
    {const: STAGE_DATA_SOURCE.real_time, title: 'Real Time'},
    stageHasPersistedData && {const: STAGE_DATA_SOURCE.time_series, title: 'Time Series'},
    canShowTelemetryServiceWarningsDataSource && processor && processorCanRaiseWarnings(processor) && {
      const: STAGE_DATA_SOURCE.telemetry_service_warnings,
      title: 'Telemetry Service Warnings'
    }
  ]),
  [canShowTelemetryServiceWarningsDataSource, processor, stageHasPersistedData]);

  const isTimeSeriesControl = stageDataSourceIsTimeSeries || usePatternWithPersistedData;
  const showPersistedDataControl = (
    stageHasPersistedData || usePatternWithPersistedData ||
    usePatternWithRawPersistedData || (processor && processorCanRaiseWarnings(processor))
  ) && (
    dataSourceOptions.length > 1 || mayCombineGraphs ||
      (isTimeSeriesControl && valueColumnOptions.length > 1) ||
      usePatternWithRawPersistedData
  );

  const result = useMemo((): FormFragmentSchema[] => [
    [
      {
        name: 'data_source',
        hidden: dataSourceOptions.length < 2,
        as: DataSourceComponent,
        schema: {
          type: 'string',
          title: 'Data Source',
          description: 'Indicates what data source should be used for the stage: real time or time-series. ' +
            'The latter one can be used only when metric logging is enabled for the stage.',
          oneOf: dataSourceOptions,
          default: DEFAULT_STAGE_DATA_SOURCE
        },
        ...shouldIncludeLabels ? {selectedValueLabel: 'Data source: '} : {},
        search: false,
        fieldProps: {className: 'data-source'}
      },
      {
        name: 'value_column_name',
        as: StageWidgetValueColumnNameInput,
        hidden: !showPersistedDataControl || !stageDataSourceIsTimeSeries ||
          valueColumnOptions.length <= 1,
        schema: {
          type: 'string',
          title: 'Value Column Name',
          description: 'For time-series data, show only one column value name',
          oneOf: valueColumnOptions,
        }
      },
      {
        name: 'combine_graphs',
        hidden: !mayCombineGraphs,
        schema: {
          type: 'string',
          title: 'Combine graphs',
          description: 'Mode of displaying multiple graphs',
          oneOf: [
            {const: COMBINE_GRAPHS_MODE.NONE, title: 'Separate graphs'},
            {const: COMBINE_GRAPHS_MODE.LINEAR, title: 'Combine graphs: Linear'},
            {const: COMBINE_GRAPHS_MODE.STACKED, title: 'Combine graphs: Stacked'},
          ],
          default: DEFAULT_COMBINE_GRAPHS_MODE
        },
        fieldProps: {className: 'combine-graphs'},
        search: false,
      },
    ],
    [
      {
        name: 'aggregation_type',
        as: AggregationTypeInput,
        hidden: !(isTimeSeriesControl && !usePatternWithRawPersistedData),
        schema: {
          type: 'string',
          title: 'Aggregation Type',
          description: '',
          oneOf: aggregationTypeOptions,
        },
        clearable: true,
        search: false,
        ...shouldIncludeLabels ? {selectedValueLabel: 'Aggregation Type: '} : {},
        fieldProps: {className: 'aggregation-type'}
      },
      {
        name: 'aggregation_period',
        as: AggregationInput,
        hidden: !(showPersistedDataControl && usePatternWithRawPersistedData && usePatternWithPersistedData) &&
          !(isTimeSeriesControl && !usePatternWithRawPersistedData),
        disabled: aggregationType === 'none',
        schema: {
          type: 'number',
          title: 'Time Series Aggregation',
          description: 'Accumulate samples within this period of time and show their average as a single item.',
          default: DEFAULT_STAGE_TIME_SERIES_AGGREGATION
        },
        aggregations: aggregationType === 'none' ?
          aggregationIntervalOptionsWithOff : aggregationIntervalOptions,
        ...shouldIncludeLabels ? {selectedValueLabel: 'Time Series Aggregation: '} : {},
      },
      {
        name: 'time_series_duration',
        as: DurationInput,
        hidden: !(showPersistedDataControl && usePatternWithRawPersistedData) &&
          !(isTimeSeriesControl && !usePatternWithRawPersistedData),
        schema: {
          type: 'number',
          title: 'Time Series Duration',
          description: 'For time-series data, show only last samples limited by duration.',
          default: DEFAULT_STAGE_TIME_SERIES_DURATION
        },
        ...shouldIncludeLabels ? {selectedValueLabel: 'Time Series Duration: '} : {},
        customValueType: 'dates',
      },
    ],
    [
      {
        name: 'anomalous_only',
        hidden: !isProbeOperational || !stageMayHaveAnomalies,
        schema: {
          type: 'boolean',
          title: 'Anomalies Only',
          description: 'Show only anomalous items.',
        },
        fieldProps: {className: 'anomalous-only'},
      },
      {
        name: 'show_context',
        hidden: !isProbeOperational || !stageHasAlternativeRenderingStrategy || alwaysUseContext,
        schema: {
          type: 'boolean',
          title: 'Show Contextual Information',
          description: patternDescription?.description ?? 'Show information from related stages.'
        },
        fieldProps: {
          className: 'show-context',
          descriptionPosition,
        },
      },
    ],
  ], [
    DataSourceComponent, aggregationType, aggregationTypeOptions, alwaysUseContext, dataSourceOptions,
    descriptionPosition, isProbeOperational, isTimeSeriesControl, mayCombineGraphs,
    patternDescription?.description, shouldIncludeLabels, showPersistedDataControl,
    stageDataSourceIsTimeSeries, stageHasAlternativeRenderingStrategy, stageMayHaveAnomalies,
    usePatternWithPersistedData, usePatternWithRawPersistedData, valueColumnOptions
  ]);
  return result;
};
